import { createReducer, on } from '@ngrx/store';
import {
  FavoriteLocation,
  Itinerary,
  Location,
  MiniDriver,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  PaymentAccount,
  PaymentCollectionType,
  Provider,
  RideEstimate,
  RideMetadataField,
  RideRequest,
  RideRequestInfo,
  RideRequestType,
  ServiceLevelType,
  TransitProgramInfo,
  TripDetail,
  TripListItem,
  VehicleType
} from '@uzurv/rideWss/clara';
import { cloneDeep, findIndex } from 'lodash';
import moment from 'moment-timezone';
import * as RiderAuthenticationActions from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.action';
import * as RidesActions from '@uzurv/rideWss/app/store/rides/rides.actions';
import { DateFormatConstants, RideStatusConstants } from '@uzurv/rideWss/app/constants';
import { isNullOrUndefined, RideUtil } from '@uzurv/rideWss/app/util';
import { AutoCompleteResult } from '@uzurv/rideWss/app/models';

export const ridesFeatureKey = 'rides';

export interface RideGroup {
  timePeriod: string;
  rideListItems: Array<TripListItem>;
}

export enum CreateItineraryUIState {
  NEW = 0,
  ADD_LEG = 1,
  RETURN_TRIP = 2
}

export enum LocationTypes {
  PICKUP = 'pickup',
  DROPOFF = 'dropoff'
}

export interface RidesState {
  isFetchingOrganizationRiderListItems: boolean;
  isInitialOrganizationRiderListItemsLoaded: boolean;
  organizationRiderListItems: Array<OrganizationRiderListItem>;
  initialUpcomingRidesLoading: boolean;
  upcomingRidesRefreshing: boolean;
  additionalUpcomingRidesLoading: boolean;
  upcomingRideGroups: Array<RideGroup>;
  upcomingRideListItems: Array<TripListItem>;
  upcomingRideListItemsPageNumber: number;
  upcomingRideListItemsPageSize: number;
  upcomingRideListItemsInfiniteScrollEnabled: boolean;
  fetchingUpcomingRideListItems: boolean;
  initialPastRidesLoading: boolean;
  pastRidesRefreshing: boolean;
  additionalPastRidesLoading: boolean;
  pastRideGroups: Array<RideGroup>;
  pastRideListItems: Array<TripListItem>;
  pastRideListItemsPageNumber: number;
  pastRideListItemsPageSize: number;
  pastRideListItemsInfiniteScrollEnabled: boolean;
  fetchingPastRideListItems: boolean;
  rideDetailLoading: boolean;
  activeRide: TripListItem;
  selectedRide: TripDetail;
  selectedRideEstimate: RideEstimate;
  selectedServiceLevelType: ServiceLevelType;
  selectedFavoriteLocation: FavoriteLocation;
  selectedFavoriteLocationSearchResult: Location;
  miniDriver: MiniDriver;
  itinerary: Itinerary;
  isRideSubscription: boolean;
  selectedDaysOfTheWeek: Array<string>;
  subscriptionStartDate: Date;
  subscriptionEndDate: Date;
  isEditingSubscriptionDetails: boolean;
  createItineraryUIState: CreateItineraryUIState;
  selectedPaymentCollectionType: PaymentCollectionType;
  selectedPaymentAccount: PaymentAccount;
  currentRideRequestLeg: RideRequest;
  closestTransitProgram: TransitProgramInfo;
  rideMetadataFields: Array<RideMetadataField>;

  availableProviders: Array<Provider>;
  organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>;
  vehicleTypes: Array<VehicleType>;
  favoriteLocationListItems: Array<FavoriteLocation>;
  rideRequestTypes: Array<RideRequestType>;
  recentLocationListItems: Array<Location>;
  submittingItinerary: boolean;
  favoriteLocationsLoading: boolean;
  addFavoriteLocationLoading: boolean;
  deleteFavoriteLocationLoading: boolean;
  recentLocationsLoading: boolean;
  autoCompleteListItems: Array<AutoCompleteResult>;
  autoCompleteListItemsLocationType: LocationTypes | null;
  disableCancel: boolean;
  disableSubmit: boolean;
  riderHasPca: boolean;
  matchingUnclaimedOrganizationRiderListItems: Array<OrganizationRiderListItem>;
  selectedOrganizationRiderListItem: OrganizationRiderListItem;
  isActionInProgress: boolean;
}

export const initialState: RidesState = {
  isFetchingOrganizationRiderListItems: false,
  isInitialOrganizationRiderListItemsLoaded: false,
  organizationRiderListItems: new Array<OrganizationRiderListItem>(),
  initialUpcomingRidesLoading: false,
  additionalUpcomingRidesLoading: false,
  upcomingRidesRefreshing: false,
  upcomingRideGroups: new Array<RideGroup>(),
  upcomingRideListItems: new Array<TripListItem>(),
  upcomingRideListItemsPageSize: 20,
  upcomingRideListItemsPageNumber: 0,
  upcomingRideListItemsInfiniteScrollEnabled: false,
  fetchingUpcomingRideListItems: false,
  initialPastRidesLoading: false,
  additionalPastRidesLoading: false,
  pastRidesRefreshing: false,
  pastRideGroups: new Array<RideGroup>(),
  pastRideListItems: new Array<TripListItem>(),
  pastRideListItemsPageSize: 20,
  pastRideListItemsPageNumber: 0,
  pastRideListItemsInfiniteScrollEnabled: false,
  fetchingPastRideListItems: false,
  rideDetailLoading: false,
  activeRide: null,
  selectedRide: null,
  selectedRideEstimate: null,
  selectedServiceLevelType: null,
  selectedFavoriteLocation: null,
  selectedFavoriteLocationSearchResult: null,
  miniDriver: null,
  itinerary: null,
  isRideSubscription: false,
  selectedDaysOfTheWeek: [],
  subscriptionStartDate: null,
  subscriptionEndDate: null,
  isEditingSubscriptionDetails: false,
  createItineraryUIState: null,
  selectedPaymentCollectionType: null,
  selectedPaymentAccount: null,
  currentRideRequestLeg: null,
  closestTransitProgram: null,
  rideMetadataFields: [],
  availableProviders: [],
  organizationRiderServiceLevels: [],
  vehicleTypes: [],
  favoriteLocationsLoading: false,
  addFavoriteLocationLoading: false,
  deleteFavoriteLocationLoading: false,
  favoriteLocationListItems: new Array<FavoriteLocation>(),
  rideRequestTypes: [],
  recentLocationsLoading: false,
  recentLocationListItems: new Array<Location>(),
  submittingItinerary: false,
  autoCompleteListItems: new Array<AutoCompleteResult>(),
  autoCompleteListItemsLocationType: null,
  disableCancel: false,
  disableSubmit: false,
  riderHasPca: false,
  matchingUnclaimedOrganizationRiderListItems: new Array<OrganizationRiderListItem>(),
  selectedOrganizationRiderListItem: null,
  isActionInProgress: false
};

export const reducer = createReducer(
  initialState,

  on(RidesActions.resetStore, (state, action) => {
    return {
      ...initialState
    };
  }),

  on(RidesActions.favoriteLocationSelected, (state, action) => {
    const selectedFavoriteLocation = state.favoriteLocationListItems.find(favoriteLocation => {
      return action.favoriteLocation.equals(favoriteLocation);
    });
    return {
      ...state,
      selectedFavoriteLocation: selectedFavoriteLocation
    };
  }),

  on(RidesActions.favoriteLocationSearchResultSelected, (state, action) => {
    return {
      ...state,
      selectedFavoriteLocationSearchResult: action.location
    };
  }),

  on(RidesActions.clearSelectedRide, (state, action) => {
    return {
      ...state,
      selectedRide: initialState.selectedRide
    };
  }),

  on(RidesActions.rideSelected, (state, action) => {
    const mergedLists: Array<TripListItem> = state.upcomingRideListItems.concat(state.pastRideListItems);
    const selectedRide = mergedLists.find(rideListItem => {
      return action.rideRequestId === rideListItem.rideRequestId;
    }) as TripDetail;
    return {
      ...state,
      selectedRide: selectedRide
    };
  }),

  on(RidesActions.fetchOrganizationRiderListItems, (state, action) => {
    return {
      ...state,
      isFetchingOrganizationRiderListItems: true
    };
  }),

  on(RidesActions.fetchOrganizationRiderListItemsSuccess, (state, action) => {
    // If the selected organization list item is unset AND the rider only has ONE organization, set that organization as selected.
    // If the selected organization list item is unset AND the rider has multiple organizations, leave it unset.
    let selectedOrganizationRiderListItem = null;

    if (action.organizationRiderListItems.length === 1) {
      selectedOrganizationRiderListItem = action.organizationRiderListItems[0];
    } else {
      if (!isNullOrUndefined(state.selectedOrganizationRiderListItem)) {
        selectedOrganizationRiderListItem = action.organizationRiderListItems?.find(
          organizationRider => organizationRider.organizationRiderId === state.selectedOrganizationRiderListItem.organizationRiderId
        );
      }
    }

    return {
      ...state,
      organizationRiderListItems: action.organizationRiderListItems,
      selectedOrganizationRiderListItem: selectedOrganizationRiderListItem,
      isFetchingOrganizationRiderListItems: false,
      isInitialOrganizationRiderListItemsLoaded: true
    };
  }),

  on(RidesActions.fetchOrganizationRiderListItemsFail, (state, action) => {
    return {
      ...state,
      isFetchingOrganizationRiderListItems: false
    };
  }),

  on(RidesActions.fetchUpcomingRideListItems, (state, action) => {
    return {
      ...state,
      fetchingUpcomingRideListItems: true
    };
  }),

  on(RidesActions.fetchInitialUpcomingRideListItems, (state, action) => {
    return {
      ...state,
      initialUpcomingRidesLoading: true
    };
  }),

  on(RidesActions.refreshUpcomingRideListItems, (state, action) => {
    return {
      ...state,
      upcomingRideListItemsInfiniteScrollEnabled: false,
      upcomingRideListItemsPageNumber: initialState.upcomingRideListItemsPageNumber,
      upcomingRideListItemsPageSize: initialState.upcomingRideListItemsPageSize,
      upcomingRidesRefreshing: true
    };
  }),

  on(RidesActions.fetchAdditionalUpcomingRideListItems, (state, action) => {
    return {
      ...state,
      upcomingRideListItemsPageNumber: state.upcomingRideListItemsPageNumber,
      upcomingRideListItemsPageSize: state.upcomingRideListItemsPageSize,
      additionalUpcomingRidesLoading: false
    };
  }),

  on(RidesActions.refreshUpcomingRideListItemsSuccess, (state, action) => {
    return {
      ...state,
      upcomingRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.upcomingRideListItemsPageSize
      ),
      upcomingRideGroups: RideUtil.groupRides(action.tripListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: action.tripListItems,
      initialUpcomingRidesLoading: false,
      additionalUpcomingRidesLoading: false,
      upcomingRidesRefreshing: false,
      upcomingRideListItemsPageNumber: state.upcomingRideListItemsPageNumber + 1
    };
  }),

  on(RidesActions.fetchUpcomingRideListItemsSuccess, (state, action) => {
    let upcomingRideListItems: Array<TripListItem> = state.upcomingRideListItems;
    action.tripListItems.forEach(rideListItem => {
      upcomingRideListItems = RideUtil.addOrReplaceRideInList(rideListItem, upcomingRideListItems, true);
    });

    return {
      ...state,
      upcomingRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.upcomingRideListItemsPageSize
      ),
      upcomingRideGroups: RideUtil.groupRides(upcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: upcomingRideListItems,
      initialUpcomingRidesLoading: false,
      additionalUpcomingRidesLoading: false,
      upcomingRidesRefreshing: false,
      upcomingRideListItemsPageNumber: state.upcomingRideListItemsPageNumber + 1
    };
  }),

  // TODO: Future way to handle this. Calculate page number so this can be more flexible
  on(RidesActions.fetchUpcomingRideListItemsSuccessFuture, (state, action) => {
    let updatedUpcomingRideListItems: Array<TripListItem> = state.upcomingRideListItems;
    let updatedPastRideListItems: Array<TripListItem> = state.pastRideListItems;
    action.tripListItems.forEach(rideListItem => {
      updatedUpcomingRideListItems = RideUtil.addOrReplaceRideInList(rideListItem, updatedUpcomingRideListItems, true);
      updatedPastRideListItems = RideUtil.removeRideFromList(rideListItem, updatedPastRideListItems);
    });

    // Calculate new page number
    const upcomingRideListPageNumber: number =
      updatedUpcomingRideListItems.length === 0 ? 0 : Math.ceil(updatedUpcomingRideListItems.length / state.upcomingRideListItemsPageSize);

    return {
      ...state,
      upcomingRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.upcomingRideListItemsPageSize
      ),
      upcomingRideGroups: RideUtil.groupRides(updatedUpcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: updatedUpcomingRideListItems,
      pastRideGroups: RideUtil.groupRides(updatedPastRideListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      pastRideListItems: updatedPastRideListItems,
      initialUpcomingRidesLoading: false,
      additionalUpcomingRidesLoading: false,
      upcomingRidesRefreshing: false,
      upcomingRideListItemsPageNumber: upcomingRideListPageNumber,
      fetchingUpcomingRideListItems: false
    };
  }),

  on(RidesActions.fetchUpcomingRideListItemsFail, (state, action) => {
    return {
      ...state,
      additionalUpcomingRidesLoading: false
    };
  }),

  on(RidesActions.fetchPastRideListItems, (state, action) => {
    return {
      ...state,
      fetchingPastRideListItems: true
    };
  }),

  on(RidesActions.fetchInitialPastRideListItems, (state, action) => {
    return {
      ...state,
      initialPastRidesLoading: true
    };
  }),

  on(RidesActions.refreshPastRideListItems, (state, action) => {
    return {
      ...state,
      pastRideListItemsInfiniteScrollEnabled: false,
      pastRideListItemsPageNumber: initialState.pastRideListItemsPageNumber,
      pastRideListItemsPageSize: initialState.pastRideListItemsPageSize,
      pastRidesRefreshing: true
    };
  }),

  on(RidesActions.fetchAdditionalPastRideListItems, (state, action) => {
    return {
      ...state,
      pastRideListItemsPageNumber: state.pastRideListItemsPageNumber,
      pastRideListItemsPageSize: state.pastRideListItemsPageSize,
      additionalPastRidesLoading: false
    };
  }),

  on(RidesActions.fetchPastRideListItemsSuccessFuture, (state, action) => {
    // TODO: Future way to handle this. Calculate page number so this can be more flexible
    let updatedUpcomingRideListItems: Array<TripListItem> = state.upcomingRideListItems;
    let updatedPastRideListItems: Array<TripListItem> = state.pastRideListItems;
    action.tripListItems.forEach(rideListItem => {
      updatedPastRideListItems = RideUtil.addOrReplaceRideInList(rideListItem, updatedPastRideListItems, false);
      updatedUpcomingRideListItems = RideUtil.removeRideFromList(rideListItem, updatedUpcomingRideListItems);
    });

    // Calculate new page number
    const pastRideListPageNumber: number =
      updatedPastRideListItems.length === 0 ? 0 : Math.ceil(updatedPastRideListItems.length / state.pastRideListItemsPageSize);

    return {
      ...state,
      pastRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.pastRideListItemsPageSize
      ),
      upcomingRideGroups: RideUtil.groupRides(updatedUpcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: updatedUpcomingRideListItems,
      pastRideGroups: RideUtil.groupRides(updatedPastRideListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      pastRideListItems: updatedPastRideListItems,
      initialPastRidesLoading: false,
      additionalPastRidesLoading: false,
      pastRidesRefreshing: false,
      pastRideListItemsPageNumber: pastRideListPageNumber,
      fetchingPastRideListItems: false
    };
  }),

  on(RidesActions.refreshPastRideListItemsSuccess, (state, action) => {
    return {
      ...state,
      pastRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.pastRideListItemsPageSize
      ),
      pastRideGroups: RideUtil.groupRides(action.tripListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      pastRideListItems: action.tripListItems,
      initialPastRidesLoading: false,
      additionalPastRidesLoading: false,
      pastRidesRefreshing: false,
      pastRideListItemsPageNumber: state.pastRideListItemsPageNumber + 1
    };
  }),

  on(RidesActions.fetchPastRideListItemsSuccess, (state, action) => {
    let pastRideListItems: Array<TripListItem> = state.pastRideListItems;
    action.tripListItems.forEach(rideListItem => {
      pastRideListItems = RideUtil.addOrReplaceRideInList(rideListItem, pastRideListItems, false);
    });

    return {
      ...state,
      pastRideListItemsInfiniteScrollEnabled: RideUtil.isInfiniteScrollEnabled(
        action.tripListItems,
        initialState.pastRideListItemsPageSize
      ),
      pastRideGroups: RideUtil.groupRides(pastRideListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      pastRideListItems: pastRideListItems,
      initialPastRidesLoading: false,
      additionalPastRidesLoading: false,
      pastRidesRefreshing: false,
      pastRideListItemsPageNumber: state.pastRideListItemsPageNumber + 1
    };
  }),

  on(RidesActions.fetchPastRideListItemsFail, (state, action) => {
    return {
      ...state,
      additionalPastRidesLoading: false
    };
  }),

  on(RidesActions.fetchRideDetail, (state, action) => {
    return {
      ...state,
      disableSubmit: true
    };
  }),

  on(RidesActions.refreshRideDetail, (state, action) => {
    return {
      ...state,
      rideDetailLoading: true,
      disableSubmit: true
    };
  }),

  on(RidesActions.fetchRideDetailSuccess, (state, action) => {
    let updatedUpcomingRideListItems: Array<TripListItem> = [];
    let updatedPastRideListItems: Array<TripListItem> = [];

    switch (action.tripDetail.status) {
      case RideStatusConstants.COMPLETED:
      case RideStatusConstants.CANCELLED:
        updatedUpcomingRideListItems = RideUtil.removeRideFromList(action.tripDetail, state.upcomingRideListItems);
        updatedPastRideListItems = RideUtil.addOrReplaceRideInList(action.tripDetail, state.pastRideListItems, false);
        break;
      default:
        updatedUpcomingRideListItems = RideUtil.addOrReplaceRideInList(action.tripDetail, state.upcomingRideListItems, true);
        updatedPastRideListItems = state.pastRideListItems;
        break;
    }

    return {
      ...state,
      upcomingRideGroups: RideUtil.groupRides(updatedUpcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      pastRideGroups: RideUtil.groupRides(updatedPastRideListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: updatedUpcomingRideListItems,
      pastRideListItems: updatedPastRideListItems,
      selectedRide: action.setSelectedRide ? action.tripDetail : state.selectedRide,
      rideDetailLoading: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.fetchRideDetailFail, (state, action) => {
    return {
      ...state,
      disableSubmit: false
    };
  }),

  on(
    RidesActions.fetchRideDetailSuccess,
    RidesActions.fetchUpcomingRideListItemsSuccess,
    RidesActions.fetchUpcomingRideListItemsSuccessFuture,
    RidesActions.refreshUpcomingRideListItemsSuccess,
    (state, _) => {
      const activeRide: TripListItem = state.upcomingRideListItems.find(ride => RideUtil.isRideInExecution(ride));

      return {
        ...state,
        activeRide: activeRide
      };
    }
  ),

  on(RidesActions.fetchRideEstimatesSuccess, (state, action) => {
    return {
      ...state,
      selectedRideEstimate: action.rideEstimate
    };
  }),

  on(RidesActions.activateWillCallRideFromList, (state, action) => {
    return {
      ...state,
      disableSubmit: true
    };
  }),

  on(RidesActions.activateWillCallRideSuccess, (state, action) => {
    const activateWillCallRideUpdatedList: Array<TripListItem> = RideUtil.addOrReplaceRideInList(
      action.tripDetail,
      state.upcomingRideListItems,
      true
    );
    return {
      ...state,
      upcomingRideGroups: RideUtil.groupRides(activateWillCallRideUpdatedList, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: activateWillCallRideUpdatedList,
      disableSubmit: false,
      selectedRide: action.tripDetail
    };
  }),

  on(RidesActions.activateWillCallRideFail, (state, action) => {
    return {
      ...state,
      disableSubmit: false
    };
  }),

  on(RidesActions.fetchClosestTransitProgramSuccess, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);

    if (!isNullOrUndefined(currentRideRequestLeg)) {
      // Null chaining operator is deliberate, a null transit program should wipe the time zone
      currentRideRequestLeg.timeZone = action.transitProgramInfo?.timeZone;

      // Trigger updated time zone adjusted pick up date
      currentRideRequestLeg.localPickUpDate = state.currentRideRequestLeg.localPickUpDate;
    }

    return {
      ...state,
      closestTransitProgram: action.transitProgramInfo,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.fetchRideMetadataFieldsForTransitProgramSuccess, (state, action) => {
    return {
      ...state,
      rideMetadataFields: action.rideMetadataFields
    };
  }),

  on(RidesActions.assignRideMetadataValue, (state, action) => {
    const clonedCurrentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    const existingMetadataValueIndex = clonedCurrentRideRequestLeg.rideMetadataValues.findIndex(
      rideMetadataValue => rideMetadataValue.rideMetadataField.id === action.rideMetadataValue.rideMetadataField.id
    );

    if (existingMetadataValueIndex !== -1) {
      clonedCurrentRideRequestLeg.rideMetadataValues[existingMetadataValueIndex] = action.rideMetadataValue;
    } else {
      clonedCurrentRideRequestLeg.rideMetadataValues.push(action.rideMetadataValue);
    }

    return {
      ...state,
      currentRideRequestLeg: clonedCurrentRideRequestLeg
    };
  }),

  on(RidesActions.removeRideMetadataValue, (state, action) => {
    const clonedCurrentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);

    clonedCurrentRideRequestLeg.rideMetadataValues = clonedCurrentRideRequestLeg.rideMetadataValues.filter(
      rideMetadataValue => rideMetadataValue.rideMetadataField.id !== action.rideMetadataValue.rideMetadataField.id
    );
    return {
      ...state,
      currentRideRequestLeg: clonedCurrentRideRequestLeg
    };
  }),

  on(RidesActions.fetchRideRequestInfoSuccess, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.riderFeeEstimate = action.rideRequestInfo.riderFeeDetail;
    currentRideRequestLeg.requestFee = action.rideRequestInfo.riderFee;
    currentRideRequestLeg.rideRequestInfo = action.rideRequestInfo;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.cancelRide, (state, action) => {
    return {
      ...state,
      disableCancel: true
    };
  }),

  on(RidesActions.cancelRideDismissed, (state, action) => {
    return {
      ...state,
      disableCancel: false
    };
  }),

  on(RidesActions.cancelRideConfirmed, (state, action) => {
    return {
      ...state,
      disableCancel: true,
      disableSubmit: true
    };
  }),

  on(RidesActions.cancelRideSuccess, (state, action) => {
    const updatedUpcomingRideListItems: Array<TripListItem> = RideUtil.removeRideFromList(action.tripDetail, state.upcomingRideListItems);
    const updatedPastRideListItems: Array<TripListItem> = RideUtil.addOrReplaceRideInList(
      action.tripDetail,
      state.pastRideListItems,
      false
    );

    // Update the ride request in the current itinerary
    const itinerary: Itinerary = cloneDeep(state.itinerary);

    if (!isNullOrUndefined(itinerary) && !isNullOrUndefined(itinerary.rideRequests)) {
      itinerary.rideRequests.forEach(rr => {
        if (rr.id === action.tripDetail.rideRequestId) {
          rr.active = false;
        }
        return rr;
      });
    }

    return {
      ...state,
      upcomingRideGroups: RideUtil.groupRides(updatedUpcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: updatedUpcomingRideListItems,
      pastRideGroups: RideUtil.groupRides(updatedPastRideListItems, DateFormatConstants.PAST_RIDE_GROUP_DATE_FORMAT),
      pastRideListItems: updatedPastRideListItems,
      selectedRide: action.tripDetail,
      itinerary: itinerary,
      disableCancel: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.cancelRideFail, (state, action) => {
    return {
      ...state,
      disableCancel: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.disableSubmit, (state, action) => {
    return {
      ...state,
      disableSubmit: action.disableSubmit
    };
  }),

  on(RidesActions.startNewItinerary, (state, action) => {
    const itinerary: Itinerary = RideUtil.initializeNewItinerary();
    const currentRideRequestLeg: RideRequest = RideUtil.initializeNewRideRequestLeg(state.selectedOrganizationRiderListItem);

    return {
      ...state,
      selectedServiceLevelType: null,
      isRideSubscription: false,
      itinerary: itinerary,
      currentRideRequestLeg: currentRideRequestLeg,
      createItineraryUIState: CreateItineraryUIState.NEW
    };
  }),

  on(RidesActions.startNewRideSubscription, (state, action) => {
    const itinerary: Itinerary = RideUtil.initializeNewItinerary();
    const currentRideRequestLeg: RideRequest = RideUtil.initializeNewRideRequestLeg(state.selectedOrganizationRiderListItem);

    return {
      ...state,
      isRideSubscription: true,
      selectedDaysOfTheWeek: action.days,
      subscriptionStartDate: action.startDate,
      subscriptionEndDate: action.endDate,
      itinerary: itinerary,
      currentRideRequestLeg: currentRideRequestLeg,
      createItineraryUIState: CreateItineraryUIState.NEW
    };
  }),

  on(RidesActions.updateRideSubscriptionDetails, (state, action) => {
    return {
      ...state,
      selectedDaysOfTheWeek: action.days,
      subscriptionStartDate: action.startDate,
      subscriptionEndDate: action.endDate,
      isEditingSubscriptionDetails: false
    };
  }),

  on(RidesActions.updateRideSubscriptionDetailsSuccess, (state, action) => {
    return {
      ...state,
      itinerary: action.itinerary
    };
  }),

  on(RidesActions.submitItinerarySuccess, (state, action) => {
    return {
      ...state,
      itinerary: action.itinerary
    };
  }),

  on(RidesActions.addRideRequestToItinerarySuccess, (state, action) => {
    const itinerary = cloneDeep(state.itinerary);
    itinerary.rideRequests.push(action.rideRequest);
    return {
      ...state,
      itinerary: itinerary
    };
  }),

  on(RidesActions.addRideRequestToItineraryFail, (state, action) => {
    return {
      ...state,
      disableSubmit: false
    };
  }),

  on(RidesActions.startNewItineraryLeg, (state, action) => {
    const currentRideRequestLeg: RideRequest = new RideRequest();
    currentRideRequestLeg.itineraryId = state.itinerary.id;
    currentRideRequestLeg.pickupLocation = state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].dropoffLocation;
    currentRideRequestLeg.riderPaymentCollectionType = state.selectedPaymentCollectionType;
    currentRideRequestLeg.billablePassengerCount =
      state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].billablePassengerCount;
    currentRideRequestLeg.passengerCount = state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].passengerCount;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg,
      closestTransitProgram: null,
      availableProviders: [],
      rideMetadataFields: [],
      createItineraryUIState: CreateItineraryUIState.ADD_LEG
    };
  }),

  on(RidesActions.processRideSubscriptionItineraryForSubmission, (state, action) => {
    let itinerary: Itinerary = cloneDeep(state.itinerary);
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    const rideRequestInfo: RideRequestInfo = cloneDeep(state.currentRideRequestLeg.rideRequestInfo);
    const organizationRiderListItem: OrganizationRiderListItem = cloneDeep(state.selectedOrganizationRiderListItem);

    itinerary = RideUtil.processItineraryForSubmission(
      itinerary,
      currentRideRequestLeg,
      rideRequestInfo,
      organizationRiderListItem,
      action.riderId
    );

    // Ride requests are now sorted. Validate the isReturnRide flag.
    const returnRideIndex: number = itinerary.rideRequests.findIndex(rideRequest => rideRequest.isReturnRide);
    if (returnRideIndex !== -1) {
      // Verify that only the last list item may be designated isReturnRide.
      if (returnRideIndex !== itinerary.rideRequests.length - 1) {
        itinerary.rideRequests[returnRideIndex].isReturnRide = false;
      }

      // Verify that the last drop-off location === first item pickup location. If not, set isReturnRide to false.
      if (
        itinerary.rideRequests[0].pickupLocation.display !==
        itinerary.rideRequests[itinerary.rideRequests.length - 1].dropoffLocation.display
      ) {
        itinerary.rideRequests[itinerary.rideRequests.length - 1].isReturnRide = false;
      }
    }


    // Apply service level logic
    itinerary.rideRequests = itinerary.rideRequests.map(rideRequest =>
      RideUtil.updateRideRequestServiceLevel(
        rideRequest,
        state.selectedServiceLevelType,
        state.selectedOrganizationRiderListItem?.organizationServiceLevels
      )
    );

    return {
      ...state,
      itinerary: itinerary
    };
  }),

  on(RidesActions.startNewItineraryReturnTrip, (state, action) => {
    const currentRideRequestLeg: RideRequest = new RideRequest();
    currentRideRequestLeg.isReturnRide = true;
    currentRideRequestLeg.itineraryId = state.itinerary.id;
    currentRideRequestLeg.pickupLocation = state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].dropoffLocation;
    currentRideRequestLeg.dropoffLocation = state.itinerary.rideRequests[0].pickupLocation;
    currentRideRequestLeg.billablePassengerCount =
      state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].billablePassengerCount;
    currentRideRequestLeg.passengerCount = state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1].passengerCount;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg,
      closestTransitProgram: null,
      availableProviders: [],
      rideMetadataFields: [],
      createItineraryUIState: CreateItineraryUIState.RETURN_TRIP
    };
  }),

  on(RidesActions.submitItinerary, (state, action) => {
    return {
      ...state,
      disableSubmit: true,
      submittingItinerary: true
    };
  }),

  on(RidesActions.submitItineraryFail, (state, action) => {
    return {
      ...state,
      disableSubmit: false,
      submittingItinerary: false
    };
  }),

  on(RidesActions.finishItinerary, RidesActions.discardItinerary, (state, action) => {
    return {
      ...state,
      itinerary: null,
      currentRideRequestLeg: null,
      closestTransitProgram: null,
      availableProviders: [],
      createItineraryUIState: null,
      isRideSubscription: false,
      selectedDaysOfTheWeek: null,
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      selectedPaymentAccount: null,
      selectedServiceLevelType: null,
      rideMetadataFields: []
    };
  }),

  on(RidesActions.selectLocation, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    if (action.locationType === LocationTypes.PICKUP) {
      currentRideRequestLeg.pickupLocation = action.rideRequestLocation;
    } else {
      currentRideRequestLeg.dropoffLocation = action.rideRequestLocation;
    }

    return {
      ...state,
      autoCompleteListItems: [],
      autoCompleteListItemsLocationType: null,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectLocationFromAutocompleteResult, (state, _) => {
    return {
      ...state,
      autoCompleteListItems: [],
      autoCompleteListItemsLocationType: null
    };
  }),

  on(RidesActions.selectRideRequestType, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.rideRequestType = state.rideRequestTypes.find(rrt => rrt.code === action.code);

    const itinerary: Itinerary = cloneDeep(state.itinerary);

    if (currentRideRequestLeg.rideRequestType?.onDemand) {
      currentRideRequestLeg.pickupDate = null;
      currentRideRequestLeg.pickupTime = null;
      currentRideRequestLeg.localPickUpDate = null;
      currentRideRequestLeg.dropoffDate = null;

      itinerary.pickupDate = new Date();
    } else {
      itinerary.pickupDate = null;
    }

    return {
      ...state,
      itinerary: itinerary,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectCurrentLegPickupDate, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.pickupDate = moment(action.date)
      .set({
        hour: moment(state.currentRideRequestLeg.localPickUpDate).hour(),
        minute: moment(state.currentRideRequestLeg.localPickUpDate).minute(),
        year: moment(state.itinerary.pickupDate).year(),
        month: moment(state.itinerary.pickupDate).month(),
        date: moment(state.itinerary.pickupDate).date()
      })
      .toDate();

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectCurrentLegPickupTime, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    if (isNullOrUndefined(action.time)) {
      currentRideRequestLeg.pickupTime = null;
      currentRideRequestLeg.localPickUpDate = null;
      return {
        ...state,
        currentRideRequestLeg: currentRideRequestLeg
      };
    }

    currentRideRequestLeg.pickupTime = action.time;
    const [hours, minutes] = action.time.split(':').map(part => parseInt(part, 10));
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    currentRideRequestLeg.localPickUpDate = moment(date)
      .set({
        year: moment(state.itinerary.pickupDate).year(),
        month: moment(state.itinerary.pickupDate).month(),
        date: moment(state.itinerary.pickupDate).date()
      })
      .toDate();

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectItineraryDate, (state, action) => {
    const itinerary: Itinerary = cloneDeep(state.itinerary);
    itinerary.pickupDate = action.date;

    itinerary.rideRequests.forEach(rideRequest => {
      if (!isNullOrUndefined(rideRequest.localPickUpDate)) {
        rideRequest.localPickUpDate = moment(rideRequest.localPickUpDate)
          .set({
            year: moment(itinerary.pickupDate).year(),
            month: moment(itinerary.pickupDate).month(),
            date: moment(itinerary.pickupDate).date()
          })
          .toDate();
      }
    });

    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    if (!isNullOrUndefined(currentRideRequestLeg)) {
      if (!currentRideRequestLeg.rideRequestType.onDemand && !isNullOrUndefined(currentRideRequestLeg.localPickUpDate)) {
        currentRideRequestLeg.localPickUpDate = moment(currentRideRequestLeg.localPickUpDate)
          .set({
            year: moment(itinerary.pickupDate).year(),
            month: moment(itinerary.pickupDate).month(),
            date: moment(itinerary.pickupDate).date()
          })
          .toDate();
      }
    }

    return {
      ...state,
      itinerary: itinerary,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectServiceLevelType, (state, action) => {
    let currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg = RideUtil.updateRideRequestServiceLevel(
      currentRideRequestLeg,
      action.serviceLevelType,
      state.selectedOrganizationRiderListItem?.organizationServiceLevels,
    );

    return {
      ...state,
      selectedServiceLevelType: action.serviceLevelType,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.setRideBackupAccepted, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.rideBackupAccepted = action.isRideBackupAccepted;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.selectPassengerCount, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.passengerCount = action.passengerCount;

    let riderHasPca = state.riderHasPca;
    const riderPaymentRequired: boolean = state.selectedOrganizationRiderListItem.riderPaymentRequired;

    if (state.riderHasPca) {
      if (currentRideRequestLeg.passengerCount > 1) {
        currentRideRequestLeg.billablePassengerCount = riderPaymentRequired ? currentRideRequestLeg.passengerCount - 1 : 0;
      } else {
        riderHasPca = false;
      }
    } else {
      currentRideRequestLeg.billablePassengerCount = riderPaymentRequired ? currentRideRequestLeg.passengerCount : 0;
    }

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg,
      riderHasPca: riderHasPca
    };
  }),

  on(RidesActions.selectRiderHasPca, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    const riderPaymentRequired: boolean = state.selectedOrganizationRiderListItem.riderPaymentRequired;

    if (action.riderHasPca && currentRideRequestLeg.passengerCount > 1) {
      currentRideRequestLeg.billablePassengerCount = riderPaymentRequired ? currentRideRequestLeg.passengerCount - 1 : 0;
    } else {
      currentRideRequestLeg.billablePassengerCount = riderPaymentRequired ? currentRideRequestLeg.passengerCount : 0;
    }

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg,
      riderHasPca: action.riderHasPca
    };
  }),

  on(RidesActions.fetchFavoriteLocations, (state, action) => {
    return {
      ...state,
      favoriteLocationsLoading: action.loading
    };
  }),

  on(RidesActions.fetchFavoriteLocationsSuccess, (state, action) => {
    const favoriteLocations: Array<FavoriteLocation> = cloneDeep(action.favoriteLocations);
    const indexOfHome = findIndex(favoriteLocations, favoriteLocation => {
      return favoriteLocation.label.toLowerCase() === 'home';
    });

    if (indexOfHome > 0) {
      const homeLocation: FavoriteLocation = favoriteLocations[indexOfHome];
      favoriteLocations.splice(indexOfHome, 1);
      favoriteLocations.splice(0, 0, homeLocation);
    }

    favoriteLocations.sort((a, b) => a.label.localeCompare(b.label));

    return {
      ...state,
      favoriteLocationListItems: favoriteLocations,
      favoriteLocationsLoading: false
    };
  }),

  on(RidesActions.fetchFavoriteLocationsFail, (state, action) => {
    return {
      ...state,
      favoriteLocationsLoading: false
    };
  }),

  on(RidesActions.fetchRideRequestTypesSuccess, (state, action) => {
    return {
      ...state,
      rideRequestTypes: action.rideRequestTypes
    };
  }),

  on(RidesActions.addFavoriteLocation, (state, action) => {
    return {
      ...state,
      addFavoriteLocationLoading: true,
      disableSubmit: true
    };
  }),

  on(RidesActions.addLocationSuccess, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    if (action.locationType === LocationTypes.PICKUP) {
      currentRideRequestLeg.pickupLocation = action.rideRequestLocation;
    } else {
      currentRideRequestLeg.dropoffLocation = action.rideRequestLocation;
    }
    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.addLocationFail, (state, action) => {
    const newState = cloneDeep(state);

    if (action.locationType === LocationTypes.PICKUP) {
      newState.currentRideRequestLeg.pickupLocation = null;
    } else {
      newState.currentRideRequestLeg.dropoffLocation = null;
    }

    return {
      ...newState,
    };
  }),

  on(RidesActions.fetchOrganizationRiderServiceLevelsSuccess, (state, action) => {
    return {
      ...state,
      organizationRiderServiceLevels: action.organizationRiderServiceLevels
    };
  }),

  on(RidesActions.addFavoriteLocationSuccess, (state, action) => {
    const favoriteLocations: Array<FavoriteLocation> = cloneDeep(state.favoriteLocationListItems);

    const existingIndex = findIndex(favoriteLocations, favoriteLocation => {
      return favoriteLocation.label.toLowerCase() === action.favoriteLocation.label.toLowerCase();
    });

    if (existingIndex === -1) {
      favoriteLocations.push(action.favoriteLocation);
    } else {
      favoriteLocations.splice(existingIndex, 1, action.favoriteLocation);
    }

    favoriteLocations.sort((a, b) => a.label.localeCompare(b.label));

    return {
      ...state,
      favoriteLocationListItems: favoriteLocations,
      addFavoriteLocationLoading: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.addFavoriteLocationFail, (state, action) => {
    return {
      ...state,
      addFavoriteLocationLoading: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.deleteFavoriteLocation, (state, action) => {
    const filteredFavoriteLocations: Array<FavoriteLocation> = state.favoriteLocationListItems.filter(
      favoriteLocation => favoriteLocation.label !== action.favoriteLocation.label
    );
    return {
      ...state,
      favoriteLocationListItems: filteredFavoriteLocations,
      deleteFavoriteLocationLoading: true,
      disableSubmit: true
    };
  }),

  on(RidesActions.deleteFavoriteLocationSuccess, (state, action) => {
    return {
      ...state,
      deleteFavoriteLocationLoading: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.deleteFavoriteLocationFail, (state, action) => {
    const favoriteLocations: Array<FavoriteLocation> = cloneDeep(state.favoriteLocationListItems);

    if (!isNullOrUndefined(action.favoriteLocation)) {
      favoriteLocations.push(action.favoriteLocation);
      favoriteLocations.sort((a, b) => a.label.localeCompare(b.label));
    }

    return {
      ...state,
      favoriteLocationListItems: favoriteLocations,
      deleteFavoriteLocationLoading: false,
      disableSubmit: false
    };
  }),

  on(RidesActions.fetchRecentLocations, (state, action) => {
    return {
      ...state,
      recentLocationsLoading: true
    };
  }),

  on(RidesActions.fetchRecentLocationsSuccess, (state, action) => {
    return {
      ...state,
      recentLocationListItems: action.locations,
      recentLocationsLoading: false
    };
  }),

  on(RidesActions.fetchRecentLocationsFail, (state, action) => {
    return {
      ...state,
      recentLocationsLoading: false
    };
  }),

  on(RidesActions.fetchAutocompleteResultsSuccess, (state, action) => {
    return {
      ...state,
      autoCompleteListItems: !isNullOrUndefined(action.autoCompleteResults) ? action.autoCompleteResults : [],
      autoCompleteListItemsLocationType: action.locationType
    };
  }),

  on(RidesActions.clearAutocompleteResults, (state, action) => {
    return {
      ...state,
      autoCompleteListItems: [],
      autoCompleteListItemsLocationType: null
    };
  }),

  on(RidesActions.updateCurrentLegDriverNote, (state, action) => {
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.riderNote = action.driverNote;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.updateRideRequestDriverNoteSuccess, RidesActions.removeRideRequestDriverNoteSuccess, (state, action) => {
    return {
      ...state,
      selectedRide: action.ride
    };
  }),

  on(RidesActions.setPaymentCollectionDetailsForNewItinerarySuccess, (state, action) => {
    const itinerary: Itinerary = cloneDeep(state.itinerary);
    const currentRideRequestLeg: RideRequest = cloneDeep(state.currentRideRequestLeg);

    const paymentCollectionType = RideUtil.extractPaymentCollectionType(action.paymentCollectionTypeCode, state.organizationRiderListItems);

    currentRideRequestLeg.riderPaymentCollectionType = paymentCollectionType;
    itinerary.riderPaymentAccountId = action.paymentAccount?.id;
    itinerary.rideRequests?.forEach(rr => {
      rr.riderPaymentCollectionType = paymentCollectionType;
    });

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg,
      itinerary: itinerary,
      selectedPaymentCollectionType: paymentCollectionType,
      selectedPaymentAccount: action.paymentAccount
    };
  }),

  on(RidesActions.fetchAvailableProvidersSuccess, (state, action) => {
    return {
      ...state,
      availableProviders: action.availableProviders
    };
  }),

  on(RidesActions.selectPreferredProvider, (state, action) => {
    const currentRideRequestLeg = cloneDeep(state.currentRideRequestLeg);
    currentRideRequestLeg.preferredProviderId = action.preferredProviderId;

    return {
      ...state,
      currentRideRequestLeg: currentRideRequestLeg
    };
  }),

  on(RidesActions.setIsEditingSubscriptionDetails, (state, action) => {
    return {
      ...state,
      isEditingSubscriptionDetails: action.isEditingSubscriptionDetails
    };
  }),

  on(RidesActions.editSubscriptionLeg, (state, action) => {
    // Set the currentRideRequestLeg as the rideRequest at index n, and remove the index at n.
    const itinerary: Itinerary = cloneDeep(state.itinerary);
    const rideRequest: RideRequest = itinerary.rideRequests[action.index];
    itinerary.rideRequests.splice(action.index, 1);

    return {
      ...state,
      itinerary: itinerary,
      currentRideRequestLeg: rideRequest
    };
  }),

  on(RidesActions.deleteSubscriptionLeg, (state, action) => {
    const itinerary: Itinerary = cloneDeep(state.itinerary);
    itinerary.rideRequests.splice(action.index, 1);
    return {
      ...state,
      itinerary: itinerary
    };
  }),

  on(RidesActions.fetchMatchingUnclaimedOrganizationRidersSuccess, (state, action) => {
    return {
      ...state,
      matchingUnclaimedOrganizationRiderListItems: action.matchingUnclaimedOrganizationRiderListItems
    };
  }),

  on(RidesActions.selectOrganizationRiderListItem, (state, action) => {
    return {
      ...state,
      selectedOrganizationRiderListItem: action.selectedOrganizationRiderListItem,
      organizationRiderServiceLevels: initialState.organizationRiderServiceLevels
    };
  }),

  on(
    RidesActions.claimOrganizationRider,
    RidesActions.ignoreOrganizationRider,
    RidesActions.acceptRideBackup,
    RidesActions.delayPickupTime,
    RidesActions.submitRideReview,
    (state, action) => {
      return {
        ...state,
        isActionInProgress: true
      };
    }
  ),

  on(RidesActions.claimOrganizationRiderSuccess, (state, action) => {
    const matchingUnclaimedOrganizationRiderListItems: Array<OrganizationRiderListItem> =
      state.matchingUnclaimedOrganizationRiderListItems.filter(
        muorli => muorli.organizationRiderId !== action.organizationRiderListItem.organizationRiderId
      );

    const organizationRiderListItems = cloneDeep(state.organizationRiderListItems);
    const existingIndex = organizationRiderListItems.findIndex(
      orli => orli.organizationRiderId === action.organizationRiderListItem.organizationRiderId
    );

    if (existingIndex === -1) {
      organizationRiderListItems.push(action.organizationRiderListItem);
    } else {
      organizationRiderListItems.splice(existingIndex, 1, action.organizationRiderListItem);
    }

    organizationRiderListItems.sort((a, b) => a.organizationName.localeCompare(b.organizationName));

    return {
      ...state,
      matchingUnclaimedOrganizationRiderListItems: matchingUnclaimedOrganizationRiderListItems,
      organizationRiderListItems: organizationRiderListItems,
      isActionInProgress: false
    };
  }),

  on(
    RidesActions.claimOrganizationRiderFail,
    RidesActions.ignoreOrganizationRiderFail,
    RidesActions.acceptRideBackupFail,
    RidesActions.delayPickupTimeFail,
    RidesActions.submitRideReviewFail,
    (state, action) => {
      return {
        ...state,
        isActionInProgress: false
      };
    }
  ),

  on(RidesActions.ignoreOrganizationRiderSuccess, (state, action) => {
    const matchingUnclaimedOrganizationRiderListItems: Array<OrganizationRiderListItem> =
      state.matchingUnclaimedOrganizationRiderListItems.filter(muorli => muorli.organizationRiderId !== action.organizationRiderId);

    return {
      ...state,
      matchingUnclaimedOrganizationRiderListItems: matchingUnclaimedOrganizationRiderListItems,
      isActionInProgress: false
    };
  }),

  on(RidesActions.acceptRideBackupSuccess, (state, action) => {
    return {
      ...state,
      selectedRide: action.ride,
      isActionInProgress: false
    };
  }),

  on(RidesActions.delayPickupTimeSuccess, (state, action) => {
    // Replace ride in the upcoming ride list
    const updatedUpcomingRideListItems = RideUtil.addOrReplaceRideInList(action.ride, state.upcomingRideListItems, true);

    return {
      ...state,
      selectedRide: action.ride,
      isActionInProgress: false,
      upcomingRideGroups: RideUtil.groupRides(updatedUpcomingRideListItems, DateFormatConstants.UPCOMING_RIDE_GROUP_DATE_FORMAT),
      upcomingRideListItems: updatedUpcomingRideListItems
    };
  }),

  on(RidesActions.submitRideReviewSuccess, (state, action) => {
    // Replace ride in the upcoming ride list
    const selectedRide = cloneDeep(state.selectedRide);
    selectedRide.rideReview = action.rideReview;

    return {
      ...state,
      selectedRide: selectedRide,
      isActionInProgress: false
    };
  }),

  // Clear data on logout
  on(RiderAuthenticationActions.logoutSuccess, (state, action) => {
    return initialState;
  })
);
